import React from 'react';
import { View, Text, StyleSheet } from 'react-native';
import Icon from 'react-native-vector-icons/MaterialCommunityIcons';
import { theme } from '../constants/theme';
import { formatCurrency } from '../utils/helpers';

interface NetProfitCardProps {
    netProfit: number;
    totalRevenue: number;
    totalExpenses: number;
    subLabel: string;
    pendingAmount?: number;
    realizedProfit?: number;
}

export const NetProfitCard: React.FC<NetProfitCardProps> = ({
    netProfit,
    totalRevenue,
    totalExpenses,
    pendingAmount,
    realizedProfit,
    subLabel,
}) => {
    const isProfit = netProfit >= 0;
    const profitColor = isProfit ? theme.colors.success : theme.colors.error;

    return (
        <View style={styles.netProfitCard}>
            <View style={styles.netProfitHeader}>
                <View style={[styles.netProfitIconContainer, { backgroundColor: profitColor + '15' }]}>
                    <Icon
                        name={isProfit ? 'trending-up' : 'trending-down'}
                        size={28}
                        color={profitColor}
                    />
                </View>
                <View style={styles.netProfitContent}>
                    <Text style={styles.netProfitLabel}>
                        {isProfit ? 'Net Profit' : 'Net Loss'}
                    </Text>
                    <Text style={styles.netProfitSubLabel}>{subLabel}</Text>
                </View>
                <Text style={[styles.netProfitValue, { color: profitColor }]}>
                    {formatCurrency(Math.abs(netProfit))}
                </Text>
            </View>
            <View style={styles.netProfitBreakdown}>
                <View style={styles.netProfitBreakdownItem}>
                    <Text style={styles.breakdownLabel}>Revenue</Text>
                    <Text style={[styles.breakdownValue, { color: theme.colors.success }]}>
                        +{formatCurrency(totalRevenue)}
                    </Text>
                </View>
                <View style={styles.netProfitBreakdownDivider} />
                <View style={styles.netProfitBreakdownItem}>
                    <Text style={styles.breakdownLabel}>Expenses</Text>
                    <Text style={[styles.breakdownValue, { color: theme.colors.error }]}>
                        -{formatCurrency(totalExpenses)}
                    </Text>
                </View>
            </View>
            {typeof pendingAmount === 'number' && typeof realizedProfit === 'number' && (
                <View style={styles.netProfitBreakdownSecondary}>
                    <View style={styles.netProfitBreakdownItem}>
                        <Text style={styles.breakdownLabel}>Pending Amount</Text>
                        <Text style={[styles.breakdownValue, { color: theme.colors.warning }]}>
                            {formatCurrency(pendingAmount)}
                        </Text>
                    </View>
                    <View style={styles.netProfitBreakdownDivider} />
                    <View style={styles.netProfitBreakdownItem}>
                        <Text style={styles.breakdownLabel}>Realized Profit</Text>
                        <Text style={[styles.breakdownValue, { color: theme.colors.primary }]}>
                            {formatCurrency(realizedProfit)}
                        </Text>
                    </View>
                </View>
            )}
        </View>
    );
};

const styles = StyleSheet.create({
    netProfitCard: {
        backgroundColor: theme.colors.surface,
        borderRadius: theme.borderRadius.xl,
        padding: theme.spacing.md,
        marginBottom: theme.spacing.md,
        ...theme.shadows.md,
        borderWidth: 1,
        borderColor: theme.colors.divider,
    },
    netProfitHeader: {
        flexDirection: 'row',
        alignItems: 'center',
    },
    netProfitIconContainer: {
        width: 48,
        height: 48,
        borderRadius: 24,
        justifyContent: 'center',
        alignItems: 'center',
        marginRight: theme.spacing.md,
    },
    netProfitContent: {
        flex: 1,
    },
    netProfitLabel: {
        ...theme.typography.body1,
        color: theme.colors.text,
        fontWeight: '700',
    },
    netProfitSubLabel: {
        ...theme.typography.caption,
        color: theme.colors.textSecondary,
        marginTop: 2,
    },
    netProfitValue: {
        ...theme.typography.h5,
        fontWeight: '800',
    },
    netProfitBreakdown: {
        flexDirection: 'row',
        marginTop: theme.spacing.md,
        paddingTop: theme.spacing.md,
        borderTopWidth: 1,
        borderTopColor: theme.colors.divider,
    },
    netProfitBreakdownSecondary: {
        flexDirection: 'row',
        marginTop: theme.spacing.sm,
        paddingTop: theme.spacing.md,
        borderTopWidth: 1,
        borderTopColor: theme.colors.divider,
    },
    netProfitBreakdownItem: {
        flex: 1,
        alignItems: 'center',
    },
    netProfitBreakdownDivider: {
        width: 1,
        height: 30,
        backgroundColor: theme.colors.divider,
    },
    breakdownLabel: {
        ...theme.typography.caption,
        color: theme.colors.textSecondary,
        marginBottom: 4,
    },
    breakdownValue: {
        ...theme.typography.body2,
        fontWeight: '700',
    },
});

export default NetProfitCard;
